import { useEffect, useRef, useState } from "react"
import { useIdleTimer } from "react-idle-timer"

/**
 * Report whether the current tab is "active" from two independent signals: tab
 * visibility and user idleness.
 *
 * The tab is reported inactive once it has been continuously hidden for
 * `hiddenGraceMs`, or once no user input has arrived for `idleTimeoutMs`. It
 * flips back to active the instant the tab is shown again or any input occurs;
 * the hidden grace period keeps a momentary alt-tab from tearing anything down.
 *
 * Idleness is delegated to `react-idle-timer`, which owns the input-event
 * wiring, throttling, and timer bookkeeping (its tracked events include
 * `visibilitychange`, so becoming visible counts as fresh activity). The
 * hidden-grace timer stays hand-rolled because it is a second, shorter
 * threshold an idle timeout cannot express: input events never fire in a
 * hidden tab, so waiting out `idleTimeoutMs` alone would keep a backgrounded
 * tab active far longer than intended. A tab that is already hidden when this
 * mounts (e.g. opened in the background) arms the grace timer immediately,
 * since no visibilitychange event will fire to announce a hidden state it was
 * already in.
 */
export default function useTabActivity(hiddenGraceMs: number, idleTimeoutMs: number): boolean {
    const [isIdle, setIsIdle] = useState(false)
    const [isHiddenPastGrace, setIsHiddenPastGrace] = useState(false)
    const hiddenTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    useIdleTimer({
        timeout: idleTimeoutMs,
        onIdle: () => setIsIdle(true),
        onActive: () => setIsIdle(false),
    })

    useEffect(() => {
        function armHiddenGraceTimer() {
            if (hiddenTimeoutRef.current) {
                clearTimeout(hiddenTimeoutRef.current)
            }
            hiddenTimeoutRef.current = setTimeout(() => {
                setIsHiddenPastGrace(true)
            }, hiddenGraceMs)
        }

        function handleVisibilityChange() {
            if (document.hidden) {
                armHiddenGraceTimer()
            } else {
                if (hiddenTimeoutRef.current) {
                    clearTimeout(hiddenTimeoutRef.current)
                    hiddenTimeoutRef.current = null
                }
                setIsHiddenPastGrace(false)
            }
        }

        document.addEventListener("visibilitychange", handleVisibilityChange)

        // A tab opened in the background is already hidden with no visibilitychange to come, so
        // arm the grace timer here or it would never go inactive from the hidden signal alone.
        if (document.hidden) {
            armHiddenGraceTimer()
        }

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange)
            if (hiddenTimeoutRef.current) {
                clearTimeout(hiddenTimeoutRef.current)
            }
        }
    }, [hiddenGraceMs])

    return !isIdle && !isHiddenPastGrace
}
